/*
  Brilinton Stability Foundation — R44-176
  One lifecycle owner for viewport metrics and layout refreshes.
  No polling and no timing-based layout guesses.
*/
(function () {
  'use strict';

  if (window.BrilintonStability) return;

  var root = document.documentElement;
  var refreshFrame = 0;
  var viewportFrame = 0;
  var lastViewport = { width: 0, height: 0 };
  var hiddenAt = 0;

  function viewportSize() {
    var visual = window.visualViewport;
    return {
      width: Math.round((visual && visual.width) || window.innerWidth || root.clientWidth || 0),
      height: Math.round((visual && visual.height) || window.innerHeight || root.clientHeight || 0)
    };
  }

  function applyViewportMetrics() {
    viewportFrame = 0;
    var next = viewportSize();
    if (!next.width || !next.height) return next;

    if (next.width !== lastViewport.width) {
      root.style.setProperty('--viewport-width', next.width + 'px');
    }
    if (next.height !== lastViewport.height) {
      root.style.setProperty('--viewport-height', next.height + 'px');
    }
    lastViewport = next;
    return next;
  }

  function scheduleViewportMetrics() {
    if (viewportFrame) return;
    viewportFrame = window.requestAnimationFrame(applyViewportMetrics);
  }

  function refresh(reason) {
    if (refreshFrame) return;
    refreshFrame = window.requestAnimationFrame(function () {
      refreshFrame = 0;
      var size = applyViewportMetrics() || viewportSize();
      window.dispatchEvent(new CustomEvent('brilinton:layout-refresh', {
        detail: {
          reason: reason || 'manual',
          width: size.width,
          height: size.height
        }
      }));

      /* Compatibility bridge only when a page actually resumes or changes
         orientation. Older components already know how to remeasure on resize;
         do not wake every legacy handler during normal initial hydration. */
      if (
        reason === 'visibility-resume' ||
        reason === 'bfcache-restore' ||
        reason === 'page-lifecycle-resume' ||
        reason === 'orientation-change'
      ) {
        window.dispatchEvent(new Event('resize'));
      }
    });
  }

  function onVisibilityChange() {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }

    /* A brief notification shade/app switch does not need a full layout pass. */
    if (!hiddenAt || Date.now() - hiddenAt >= 750) refresh('visibility-resume');
    hiddenAt = 0;
  }

  function onPageShow(event) {
    refresh(event && event.persisted ? 'bfcache-restore' : 'pageshow');
  }

  function onOrientationChange() {
    refresh('orientation-change');
  }

  function onFontLoadingDone() {
    refresh('fonts-loaded');
  }

  window.BrilintonStability = {
    refresh: refresh,
    updateViewport: scheduleViewportMetrics,
    viewport: viewportSize
  };

  /* Establish safe metrics before layout-sensitive components initialize. */
  applyViewportMetrics();

  window.addEventListener('pageshow', onPageShow, { passive: true });
  window.addEventListener('orientationchange', onOrientationChange, { passive: true });
  window.addEventListener('resize', scheduleViewportMetrics, { passive: true });
  document.addEventListener('visibilitychange', onVisibilityChange, { passive: true });
  document.addEventListener('freeze', function () { hiddenAt = Date.now(); }, { passive: true });
  document.addEventListener('resume', function () { refresh('page-lifecycle-resume'); }, { passive: true });
  document.addEventListener('shopify:section:load', function () { refresh('shopify-section-load'); });

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleViewportMetrics, { passive: true });
    window.visualViewport.addEventListener('scroll', scheduleViewportMetrics, { passive: true });
  }

  if (document.fonts) {
    if (typeof document.fonts.addEventListener === 'function') {
      document.fonts.addEventListener('loadingdone', onFontLoadingDone);
    }
    if (document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(function () { refresh('fonts-ready'); }).catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh('dom-ready'); }, { once: true });
  } else {
    refresh('script-ready');
  }
})();
